import $ from "jquery";
import * as z from "zod/mini";

import * as blueslip from "./blueslip.ts";
import * as browser_history from "./browser_history.ts";
import * as channel from "./channel.ts";
import * as hash_util from "./hash_util.ts";
import * as message_helper from "./message_helper.ts";
import * as message_store from "./message_store.ts";
import type {Message} from "./message_store.ts";
import {raw_message_schema} from "./message_store.ts";

let active_left_message_id: number | undefined;
let active_right_message_id: number | undefined;
let default_left_sidebar_html: string | undefined;
let default_right_sidebar_html: string | undefined;

const RETURNLEFT_LINK_REGEX = /##RETURNLEFT/i;
const RETURNRIGHT_LINK_REGEX = /##RETURNRIGHT/i;
const CTRLLEFT_LINK_REGEX = /##CTRLLEFT-(\d+)/i;
const CTRLRIGHT_LINK_REGEX = /##CTRLRIGHT-(\d+)/i;

const messageLinkContainerClass = "ctrl-sidebar-message-link-container";
const leftLinkClass = "ctrl-left-sidebar-message-link";
const rightLinkClass = "ctrl-right-sidebar-message-link";

const fetch_message_response_schema = z.object({
    messages: z.array(raw_message_schema),
});

// Helper function to store default sidebar HTML
function store_sidebar_html(selector: string, storage_var: "left" | "right"): void {
    const $sidebar = $(selector);
    if ($sidebar.length > 0) {
        const html = $sidebar.html();
        if (storage_var === "left") {
            default_left_sidebar_html = html;
        } else {
            default_right_sidebar_html = html;
        }
    }
}

// Store default sidebar states on first load
function store_default_sidebars(): void {
    if (default_left_sidebar_html === undefined) {
        store_sidebar_html("#left-sidebar", "left");
    }
    
    if (default_right_sidebar_html === undefined) {
        store_sidebar_html("#right-sidebar", "right");
    }
}

// Replace left sidebar content with ctrl_left_html from message
export function activate_left_sidebar(message: Message): void {
    const ctrl_left_html = message.ctrl_left_html;
    if (!ctrl_left_html) {
        return;
    }
    
    store_default_sidebars();
    
    const $leftSidebar = $("#left-sidebar");
    if ($leftSidebar.length === 0) {
        return;
    }
    
    // Replace sidebar content
    $leftSidebar.html(ctrl_left_html);
    active_left_message_id = message.id;
}

// Replace right sidebar content with ctrl_right_html from message
export function activate_right_sidebar(message: Message): void {
    const ctrl_right_html = message.ctrl_right_html;
    if (!ctrl_right_html) {
        return;
    }
    
    store_default_sidebars();
    
    const $rightSidebar = $("#right-sidebar");
    if ($rightSidebar.length === 0) {
        return;
    }
    
    // Replace sidebar content
    $rightSidebar.html(ctrl_right_html);
    active_right_message_id = message.id;
}

// Restore left sidebar to default state
export function restore_left_sidebar(): void {
    if (default_left_sidebar_html === undefined) {
        return;
    }
    
    const $leftSidebar = $("#left-sidebar");
    if ($leftSidebar.length === 0) {
        return;
    }
    
    // Fetch fresh sidebar content from server
    fetch_and_restore_left_sidebar();
}

// Restore right sidebar to default state
export function restore_right_sidebar(): void {
    if (default_right_sidebar_html === undefined) {
        return;
    }
    
    const $rightSidebar = $("#right-sidebar");
    if ($rightSidebar.length === 0) {
        return;
    }
    
    // Fetch fresh sidebar content from server
    fetch_and_restore_right_sidebar();
}

// Restore both sidebars (for Escape key)
export function restore_both_sidebars(): void {
    restore_left_sidebar();
    restore_right_sidebar();
}

// Helper function to fetch and restore a sidebar
function fetch_and_restore_sidebar(
    selector: string,
    default_html: string | undefined,
    active_message_id_setter: () => void,
    event_name: string,
): void {
    const $sidebar = $(selector);
    if ($sidebar.length > 0 && default_html !== undefined) {
        $sidebar.html(default_html);
        active_message_id_setter();

        // Re-initialize sidebar components if needed
        $(document).trigger(event_name);
    }
}

// Fetch fresh left sidebar from server and restore it
function fetch_and_restore_left_sidebar(): void {
    fetch_and_restore_sidebar(
        "#left-sidebar",
        default_left_sidebar_html,
        () => {
            active_left_message_id = undefined;
        },
        "left_sidebar_restored",
    );
}

// Fetch fresh right sidebar from server and restore it
function fetch_and_restore_right_sidebar(): void {
    fetch_and_restore_sidebar(
        "#right-sidebar",
        default_right_sidebar_html,
        () => {
            active_right_message_id = undefined;
        },
        "right_sidebar_restored",
    );
}

// Build anchor link for ctrl left sidebar
function buildCtrlLeftLinkHref(message: Message): string {
    const baseUrl = message.url ?? hash_util.by_conversation_and_time_url(message);
    return `${baseUrl}##CTRLLEFT-${message.id}`;
}

// Build anchor link for ctrl right sidebar
function buildCtrlRightLinkHref(message: Message): string {
    const baseUrl = message.url ?? hash_util.by_conversation_and_time_url(message);
    return `${baseUrl}##CTRLRIGHT-${message.id}`;
}

// Extract message ID from CTRLLEFT link
function extractCtrlLeftMessageId(value: string): number | undefined {
    const match = value.match(CTRLLEFT_LINK_REGEX);
    if (match === null) {
        return undefined;
    }
    const messageId = Number.parseInt(match[1]!, 10);
    if (Number.isNaN(messageId)) {
        return undefined;
    }
    return messageId;
}

// Extract message ID from CTRLRIGHT link
function extractCtrlRightMessageId(value: string): number | undefined {
    const match = value.match(CTRLRIGHT_LINK_REGEX);
    if (match === null) {
        return undefined;
    }
    const messageId = Number.parseInt(match[1]!, 10);
    if (Number.isNaN(messageId)) {
        return undefined;
    }
    return messageId;
}

// Clean ctrl sidebar hash from URL
function cleanCtrlSidebarHash(hash: string, type: "left" | "right"): void {
    const regex = type === "left" ? CTRLLEFT_LINK_REGEX : CTRLRIGHT_LINK_REGEX;
    const index = hash.search(regex);
    if (index === -1) {
        return;
    }
    const baseHash = hash.slice(0, index);
    const baseUrl = window.location.href.split("#")[0] + baseHash;
    try {
        window.history.replaceState(window.history.state, "", baseUrl);
    } catch (error) {
        blueslip.warn("Unable to clean ctrl sidebar hash", {hash, error});
    }
}

// Render anchor links in message content
function renderMessageCtrlSidebarLinks($content: JQuery, message: Message): void {
    const hasLeft = message.ctrl_left_html !== undefined && message.ctrl_left_html !== null;
    const hasRight = message.ctrl_right_html !== undefined && message.ctrl_right_html !== null;

    if (!hasLeft && !hasRight) {
        removeMessageCtrlSidebarLinks($content, message.id);
        return;
    }

    const selector = `.${messageLinkContainerClass}[data-ctrl-message-id='${message.id}']`;
    let $existing = $content.find(selector);

    if ($existing.length === 0) {
        const $container = $("<p>")
            .addClass(messageLinkContainerClass)
            .attr("data-ctrl-message-id", message.id.toString());
        $content.append($container);
        $existing = $container;
    }

    $existing.empty();

    if (hasLeft) {
        const leftHref = buildCtrlLeftLinkHref(message);
        const $leftAnchor = $("<a>")
            .addClass(leftLinkClass)
            .attr("href", leftHref)
            .attr("data-ctrl-message-id", message.id.toString())
            .text(`##CTRLLEFT-${message.id}`);
        $existing.append($leftAnchor);
        if (hasRight) {
            $existing.append(" ");
        }
    }

    if (hasRight) {
        const rightHref = buildCtrlRightLinkHref(message);
        const $rightAnchor = $("<a>")
            .addClass(rightLinkClass)
            .attr("href", rightHref)
            .attr("data-ctrl-message-id", message.id.toString())
            .text(`##CTRLRIGHT-${message.id}`);
        $existing.append($rightAnchor);
    }
}

// Remove anchor links from message content
function removeMessageCtrlSidebarLinks($content: JQuery, messageId: number): void {
    $content.find(`.${messageLinkContainerClass}[data-ctrl-message-id='${messageId}']`).remove();
}

// Show left sidebar for a specific message ID
function showLeftSidebarForMessageId(messageId: number): void {
    const message = message_store.get(messageId);
    if (message !== undefined) {
        activate_left_sidebar(message);
        return;
    }

    // Message not in store, fetch it
    channel.get({
        url: "/json/messages",
        data: {
            allow_empty_topic_name: true,
            message_ids: JSON.stringify([messageId]),
        },
        success(raw_data) {
            let parsed;
            try {
                parsed = fetch_message_response_schema.parse(raw_data);
            } catch (error) {
                blueslip.warn("Failed to parse ctrl left sidebar message response", {
                    message_id: messageId,
                    error,
                });
                return;
            }
            if (parsed.messages.length === 0) {
                blueslip.warn("Ctrl left sidebar message response did not include any messages", {
                    message_id: messageId,
                });
                return;
            }
            const processed = message_helper.process_new_server_message(parsed.messages[0]!);
            activate_left_sidebar(processed);
        },
        error(xhr) {
            blueslip.warn("Unable to fetch ctrl left sidebar message", {
                message_id: messageId,
                status: xhr?.status,
            });
        },
    });
}

// Show right sidebar for a specific message ID
function showRightSidebarForMessageId(messageId: number): void {
    const message = message_store.get(messageId);
    if (message !== undefined) {
        activate_right_sidebar(message);
        return;
    }

    // Message not in store, fetch it
    channel.get({
        url: "/json/messages",
        data: {
            allow_empty_topic_name: true,
            message_ids: JSON.stringify([messageId]),
        },
        success(raw_data) {
            let parsed;
            try {
                parsed = fetch_message_response_schema.parse(raw_data);
            } catch (error) {
                blueslip.warn("Failed to parse ctrl right sidebar message response", {
                    message_id: messageId,
                    error,
                });
                return;
            }
            if (parsed.messages.length === 0) {
                blueslip.warn("Ctrl right sidebar message response did not include any messages", {
                    message_id: messageId,
                });
                return;
            }
            const processed = message_helper.process_new_server_message(parsed.messages[0]!);
            activate_right_sidebar(processed);
        },
        error(xhr) {
            blueslip.warn("Unable to fetch ctrl right sidebar message", {
                message_id: messageId,
                status: xhr?.status,
            });
        },
    });
}

// Handle hash from location for ctrl sidebars
function handleCtrlSidebarHashFromLocation(): void {
    const hash = window.location.hash;

    const leftMessageId = extractCtrlLeftMessageId(hash);
    if (leftMessageId !== undefined) {
        showLeftSidebarForMessageId(leftMessageId);
        cleanCtrlSidebarHash(hash, "left");
        return;
    }

    const rightMessageId = extractCtrlRightMessageId(hash);
    if (rightMessageId !== undefined) {
        showRightSidebarForMessageId(rightMessageId);
        cleanCtrlSidebarHash(hash, "right");
    }
}

// Export function to ensure message has ctrl sidebar links
export function ensure_message_has_ctrl_sidebar_links($content: JQuery, message: Message): void {
    renderMessageCtrlSidebarLinks($content, message);
}

// Handle clicks on ##RETURNLEFT and ##RETURNRIGHT links
$(document).on("click", "a[href*='##RETURN']", function (this: HTMLAnchorElement, event) {
    const href = this.getAttribute("href");
    if (href === null) {
        return;
    }

    if (RETURNLEFT_LINK_REGEX.test(href)) {
        event.preventDefault();
        restore_left_sidebar();
        return;
    }

    if (RETURNRIGHT_LINK_REGEX.test(href)) {
        event.preventDefault();
        restore_right_sidebar();
        return;
    }
});

// Handle clicks on ##CTRLLEFT-{id} and ##CTRLRIGHT-{id} anchor links
$(document).on(
    "click",
    "a[href*='##CTRLLEFT-'], a[href*='##CTRLRIGHT-'], a[href*='##ctrlleft-'], a[href*='##ctrlright-']",
    function (this: HTMLAnchorElement, event) {
        if (event.isDefaultPrevented()) {
            return;
        }
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }
        if (event.which !== 1) {
            return;
        }
        const href = this.getAttribute("href");
        if (href === null) {
            return;
        }
        let url: URL | undefined;
        try {
            url = new URL(href, window.location.href);
        } catch {
            url = undefined;
        }
        if (url !== undefined && url.origin !== window.location.origin) {
            return;
        }
        const rawHash = url?.hash ?? href;
        
        // Check for CTRLLEFT link
        const leftTarget = extractCtrlLeftMessageId(rawHash);
        if (leftTarget !== undefined) {
            event.preventDefault();
            const baseHash = rawHash.replace(CTRLLEFT_LINK_REGEX, "");
            browser_history.go_to_location(baseHash === "" ? "#" : baseHash);
            showLeftSidebarForMessageId(leftTarget);
            return;
        }
        
        // Check for CTRLRIGHT link
        const rightTarget = extractCtrlRightMessageId(rawHash);
        if (rightTarget !== undefined) {
            event.preventDefault();
            const baseHash = rawHash.replace(CTRLRIGHT_LINK_REGEX, "");
            browser_history.go_to_location(baseHash === "" ? "#" : baseHash);
            showRightSidebarForMessageId(rightTarget);
        }
    },
);

// Handle clicks on ctrlleft/ctrlright blocks in messages
export function handle_sidebar_control_click(message_id: number, control_type: "left" | "right"): void {
    const message = message_store.get(message_id);
    if (message !== undefined) {
        if (control_type === "left" && message.ctrl_left_html) {
            activate_left_sidebar(message);
        } else if (control_type === "right" && message.ctrl_right_html) {
            activate_right_sidebar(message);
        }
        return;
    }

    // Message not in store, fetch it
    channel.get({
        url: "/json/messages",
        data: {
            allow_empty_topic_name: true,
            message_ids: JSON.stringify([message_id]),
        },
        success(raw_data) {
            let parsed;
            try {
                parsed = fetch_message_response_schema.parse(raw_data);
            } catch (error) {
                blueslip.warn("Failed to parse sidebar control message response", {
                    message_id,
                    error,
                });
                return;
            }
            if (parsed.messages.length === 0) {
                blueslip.warn("Sidebar control message response did not include any messages", {
                    message_id,
                });
                return;
            }
            const processed = message_helper.process_new_server_message(parsed.messages[0]!);
            if (control_type === "left" && processed.ctrl_left_html) {
                activate_left_sidebar(processed);
            } else if (control_type === "right" && processed.ctrl_right_html) {
                activate_right_sidebar(processed);
            }
        },
        error(xhr) {
            blueslip.warn("Unable to fetch sidebar control message", {
                message_id,
                status: xhr?.status,
            });
        },
    });
}

// Handle Escape key to restore both sidebars
$(document).on("keydown", (event) => {
    if (event.key === "Escape") {
        // Only restore if at least one sidebar is active
        if (active_left_message_id !== undefined || active_right_message_id !== undefined) {
            restore_both_sidebars();
        }
    }
});

// Handle hash changes for ctrl sidebar navigation
$(window).on("hashchange", () => {
    handleCtrlSidebarHashFromLocation();
});

// Initialize on document ready
$(() => {
    store_default_sidebars();
    // Check for ctrl sidebar hash on initial load
    handleCtrlSidebarHashFromLocation();
});
